import { Component } from '@theme/component';

/**
 * Displays volume pricing table with expandable rows.
 * Shows pricing tiers based on quantity thresholds.
 *
 * @extends {Component}
 */
class B2BVolumePricingComponent extends Component {
  constructor() {
    super();
    this.pricingData = null;
    this.typeOptionIndex = -1;
    this.currencySymbol = '';
  }

  connectedCallback() {
    super.connectedCallback();

    // Read initial data from script tag
    const scriptTag = this.querySelector('script[data-volume-pricing-json]');
    if (scriptTag) {
      try {
        this.pricingData = JSON.parse(scriptTag.textContent || '{}');
      } catch (e) {
        console.error('Error parsing volume pricing JSON:', e);
      }
    }

    // Read attributes
    this.typeOptionIndex = parseInt(this.getAttribute('data-type-option-index') || '-1', 10);
    this.currencySymbol = this.getAttribute('data-currency-symbol') || '$';

    // Bind event handlers
    this._onVariantUpdate = this.onVariantUpdate.bind(this);
    document.addEventListener('variant:update', this._onVariantUpdate);

    this._onOptionChange = (event) => {
      const variantPicker = event.target.closest('variant-picker');
      if (variantPicker) {
        // Run on next tick to allow the inputs' selected state to update
        setTimeout(() => {
          const variant = this.getSelectedVariant();
          if (variant) {
            this.updatePricing(variant);
          }
        }, 0);
      }
    };
    document.addEventListener('change', this._onOptionChange);

    // Initial load: update pricing immediately on the next tick
    setTimeout(() => {
      const initialVariant = this.getSelectedVariant();
      if (initialVariant) {
        this.updatePricing(initialVariant);
      }
    }, 0);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('variant:update', this._onVariantUpdate);
    document.removeEventListener('change', this._onOptionChange);
  }

  /**
   * Resolves the currently selected variant on the client-side.
   * @returns {Object|null} The selected variant object or null.
   */
  getSelectedVariant() {
    const variantPicker = document.querySelector('variant-picker');
    if (!variantPicker) return null;

    const selectedOptions = [];
    
    // 1. Read checked inputs
    variantPicker.querySelectorAll('fieldset[data-fieldset-index]').forEach((fieldset) => {
      const index = parseInt(fieldset.getAttribute('data-fieldset-index') || '0', 10);
      const checkedInput = fieldset.querySelector('input:checked');
      if (checkedInput) {
        selectedOptions[index] = checkedInput.value;
      }
    });

    // 2. Read select dropdowns
    variantPicker.querySelectorAll('select').forEach((select, index) => {
      selectedOptions[index] = select.value;
    });

    // 3. Match against the variants JSON
    const allVariantsScript = variantPicker.querySelector('script[data-all-variants]');
    if (allVariantsScript) {
      try {
        const variants = JSON.parse(allVariantsScript.textContent || '[]');
        return variants.find((variant) => {
          return variant.options && variant.options.every((opt, idx) => opt === selectedOptions[idx]);
        });
      } catch (e) {
        console.error('Error parsing variants JSON:', e);
      }
    }
    return null;
  }

  /**
   * Handles the variant update event (as a fallback or for external updates).
   * @param {CustomEvent} event - The variant update event.
   */
  onVariantUpdate(event) {
    const variant = event.detail.resource;
    if (variant) {
      this.updatePricing(variant);
    }
  }

  /**
   * Dynamically renders pricing tiers or fallback default price.
   * @param {Object} variant - The variant object.
   */
  updatePricing(variant) {
    const listWrapper = this.querySelector('.volume-pricing-list-wrapper');
    if (!listWrapper) return;

    let tiers = null;

    if (this.pricingData) {
      // Check if the pricingData is nested or flat
      const isNested = Object.values(this.pricingData).some(val => val && typeof val === 'object');

      if (isNested) {
        if (this.typeOptionIndex !== -1 && variant.options && variant.options[this.typeOptionIndex]) {
          const typeValue = variant.options[this.typeOptionIndex];
          const normalizedType = typeValue.toLowerCase().replace(/\s+/g, '');
          
          // Find matching key (case-insensitive and whitespace-insensitive)
          const matchedKey = Object.keys(this.pricingData).find(key => key.toLowerCase().replace(/\s+/g, '') === normalizedType);
          if (matchedKey) {
            tiers = this.pricingData[matchedKey];
          }
        }
      } else {
        // Flat (legacy format)
        tiers = this.pricingData;
      }
    }

    if (tiers && Object.keys(tiers).length > 0) {
      // Render B2B tiers
      let html = '';
      Object.entries(tiers).forEach(([range, priceVal]) => {
        const rangeLowercase = range.toLowerCase();
        
        // Match Liquid rounding behavior: parse float, round to 2 decimals, convert back to string
        const roundedPrice = (Math.round(parseFloat(priceVal) * 100) / 100).toString();
        const [numPart, fraction] = roundedPrice.split('.');

        html += `
          <div class="price-tier${rangeLowercase === 'sample' ? ' price-tier--sample' : ''}">
            <div class="tier-label">${range}${rangeLowercase !== 'sample' ? ' pieces' : ''}</div>
            <div class="main-price-value">
              <span class="price-symbol">${this.currencySymbol}</span>
              <span class="num-part">${numPart}</span>
              ${fraction ? `<span class="price-fraction">${fraction}</span>` : ''}
            </div>
          </div>
        `;
      });
      listWrapper.innerHTML = html;
    } else {
      // Fallback: render default price
      const formattedPrice = (variant.price / 100).toFixed(2);
      const [numPart, fraction] = formattedPrice.split('.');

      listWrapper.innerHTML = `
        <div class="default-price-display">
          <div class="tier-label">Price</div>
          <div class="main-price-value">
            <span class="price-symbol">${this.currencySymbol}</span>
            <span class="num-part">${numPart}</span>
            ${fraction ? `<span class="price-fraction">${fraction}</span>` : ''}
          </div>
        </div>
      `;
    }
  }

  /**
   * Toggles the expanded state of the volume pricing table
   */
  toggleExpanded() {
    this.classList.toggle('volume-pricing--expanded');
  }
}

if (!customElements.get('b2b-volume-pricing')) {
  customElements.define('b2b-volume-pricing', B2BVolumePricingComponent);
}
